import React, { useState } from 'react';
import './ImpactSummaryTab.css';

const ImpactSummaryTab = ({ summaryData }) => {
  // Function to render markdown content (basic markdown rendering)
  const renderMarkdown = (content) => {
    if (!content) return null;
    
    // Convert markdown to basic HTML
    let html = content
      // Headers
      .replace(/^### (.*$)/gim, '<h3>$1</h3>')
      .replace(/^## (.*$)/gim, '<h2>$1</h2>')
      // Bold
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      // Lists
      .replace(/^- (.*$)/gim, '<li>$1</li>')
      .replace(/^\d+\. (.*$)/gim, '<li>$1</li>')
      // Line breaks
      .replace(/\n\n/g, '</p><p>')
      .replace(/\n/g, '<br>');
    
    // Wrap list items
    html = html.replace(/(<li>.*<\/li>)/gs, '<ul>$1</ul>');
    // Wrap paragraphs
    html = '<p>' + html + '</p>';
    
    return <div className="markdown-content" dangerouslySetInnerHTML={{ __html: html }} />;
  };

  // Component to render summary text with first 3 lines preview and expandable "more..." section
  const SummaryTextRenderer = ({ text }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    
    if (!text) return null;
    
    // Split text into lines (keep all lines including empty ones)
    let lines = text.split('\n');
    
    // Remove "Impact analysis summary" header if it appears in the text
    lines = lines.filter((line, index) => {
      // Skip header lines that match "Impact analysis summary" (case insensitive)
      // This includes both plain text and markdown headers (## Impact analysis summary)
      const trimmed = line.trim();
      // Match plain text "Impact analysis summary"
      if (trimmed.match(/^Impact\s+analysis\s+summary$/i)) {
        return false;
      }
      // Match markdown headers like "## Impact analysis summary" or "### Impact analysis summary"
      if (trimmed.match(/^#{1,6}\s*Impact\s+analysis\s+summary$/i)) {
        return false;
      }
      // Also skip empty lines right after the header
      if (index > 0) {
        const prevTrimmed = lines[index - 1]?.trim();
        if (trimmed === '' && (
          prevTrimmed?.match(/^Impact\s+analysis\s+summary$/i) ||
          prevTrimmed?.match(/^#{1,6}\s*Impact\s+analysis\s+summary$/i)
        )) {
          return false;
        }
      }
      return true;
    });
    
    // Show only first 3 lines initially
    const previewLines = lines.slice(0, 3);
    const remainingLines = lines.slice(3);
    const hasMore = remainingLines.length > 0 && remainingLines.some(line => line.trim() !== '');
    
    // Join preview lines (preserve line breaks)
    const previewText = previewLines.join('\n');
    // Only show remaining lines when expanded, not the full text
    const remainingText = remainingLines.join('\n').trim();
    
    return (
      <div className="summary-text-renderer" style={{ marginBottom: '24px' }}>
        <div className="summary-preview">
          {renderMarkdown(previewText)}
        </div>
        {hasMore && !isExpanded && (
          <div style={{ marginTop: '8px' }}>
            <button
              onClick={() => setIsExpanded(true)}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--file-selected)',
                cursor: 'pointer',
                textDecoration: 'underline',
                fontSize: '14px',
                padding: '4px 0',
                fontWeight: '500',
                fontFamily: 'var(--font-small)'
              }}
            >
              more...
            </button>
          </div>
        )}
        {hasMore && isExpanded && (
          <div className="summary-full-content" style={{ marginTop: '12px' }}>
            {renderMarkdown(remainingText)}
            <div style={{ marginTop: '8px' }}>
              <button
                onClick={() => setIsExpanded(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#007ACC',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  fontSize: '14px',
                  padding: '4px 0',
                  fontWeight: '500'
                }}
              >
                show less
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Debug: Log what we received
  console.log("=== ImpactSummaryTab DEBUG ===");
  console.log("summaryData:", summaryData);
  console.log("summaryData keys:", summaryData ? Object.keys(summaryData) : "N/A");
  console.log("Has paragraph_summary:", !!summaryData?.paragraph_summary);
  console.log("Has impact_summary:", !!summaryData?.impact_summary);
  console.log("Has summary_text:", !!summaryData?.summary_text);

  if (!summaryData) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-light)' }}>
        No summary data available
      </div>
    );
  }

  // Handle different data structures
  const impactSummary = summaryData.impact_summary || summaryData;
  const paragraphSummary = summaryData.paragraph_summary;

  return (
    <div className="impact-summary-tab">
      <div className="summary-container">
        {impactSummary.error ? (
          <div style={{ padding: '20px', backgroundColor: 'rgba(255, 152, 0, 0.1)', borderRadius: '8px', border: '1px solid var(--warning-orange)', color: 'var(--warning-orange)' }}>
            <strong style={{ fontFamily: 'var(--font-big)' }}>⚠️ {impactSummary.error}</strong>
            <p style={{ marginTop: '8px', marginBottom: 0, fontFamily: 'var(--font-small)', color: 'var(--text-light)' }}>{impactSummary.message}</p>
          </div>
        ) : (
          <>
            {/* Paragraph Summary (GPT-4o-mini generated) - Show first 3 lines with expandable more... */}
            {/* Only show paragraph_summary if it exists and is different from summary_text */}
            {paragraphSummary && (
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ marginTop: 0, marginBottom: '12px', color: 'white', fontFamily: 'var(--font-big)' }}>📊 Impact Analysis Summary</h3>
                <SummaryTextRenderer text={paragraphSummary} />
              </div>
            )}

            {/* Summary Text (for both RAG and basic summaries) - Only show if paragraph_summary doesn't exist or content is different */}
            {impactSummary?.summary_text && !paragraphSummary && (
              <SummaryTextRenderer text={impactSummary.summary_text} />
            )}

            {/* Impacted Functions Summary */}
            {impactSummary.impacted_functions_summary && impactSummary.impacted_functions_summary.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ marginTop: 0, marginBottom: '12px', color: 'white', fontFamily: 'var(--font-big)' }}>Top Impacted Functions</h3>
                <div style={{ maxHeight: '500px', overflow: 'auto' }}>
                  {impactSummary.impacted_functions_summary.slice(0, 20).map((func, index) => (
                    <div key={index} style={{
                      padding: '12px',
                      marginBottom: '8px',
                      backgroundColor: 'var(--bg-gray-dark-1)',
                      borderRadius: '8px',
                      border: '1px solid var(--border-light)'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <strong style={{ color: 'white', fontFamily: 'var(--font-big)' }}>{func.modified_function}</strong>
                        <span style={{
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: '500',
                          fontFamily: 'var(--font-small)',
                          backgroundColor: func.risk_level === 'high' ? 'rgba(244, 67, 54, 0.2)' : 
                                           func.risk_level === 'medium' ? 'rgba(255, 152, 0, 0.2)' : 'rgba(76, 175, 80, 0.2)',
                          color: func.risk_level === 'high' ? 'var(--error-red)' : 
                                 func.risk_level === 'medium' ? 'var(--warning-orange)' : 'var(--success-green)',
                          border: `1px solid ${func.risk_level === 'high' ? 'var(--error-red)' : 
                                           func.risk_level === 'medium' ? 'var(--warning-orange)' : 'var(--success-green)'}`
                        }}>
                          {func.risk_level?.toUpperCase() || 'LOW'} RISK
                        </span>
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '4px', fontFamily: 'var(--font-small)' }}>
                        📄 {func.modified_file}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--text-light)', fontFamily: 'var(--font-small)' }}>
                        Affects {func.total_affected_functions} function(s) • 
                        Upstream: {func.upstream_impact} • 
                        Downstream: {func.downstream_impact}
                      </div>
                      {func.affected_functions_preview && func.affected_functions_preview.length > 0 && (
                        <div style={{ marginTop: '8px', fontSize: '11px', color: 'var(--text-light)', fontFamily: 'var(--font-small)' }}>
                          Affected: {func.affected_functions_preview.join(', ')}
                          {func.total_affected_functions > func.affected_functions_preview.length && 
                            ` ... and ${func.total_affected_functions - func.affected_functions_preview.length} more`}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Critical Functions */}
            {impactSummary.critical_functions && impactSummary.critical_functions.length > 0 && (
              <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: 'rgba(244, 67, 54, 0.1)', borderRadius: '8px', border: '1px solid var(--error-red)' }}>
                <h3 style={{ marginTop: 0, marginBottom: '12px', color: 'white', fontFamily: 'var(--font-big)' }}>🔴 Critical Functions</h3>
                <ul style={{ margin: 0, paddingLeft: '20px', color: 'var(--text-light)' }}>
                  {impactSummary.critical_functions.map((func, index) => (
                    <li key={index} style={{ marginBottom: '8px', color: 'var(--text-light)', fontFamily: 'var(--font-small)' }}>
                      <strong style={{ color: 'white' }}>{func.function_name}</strong> ({func.file}) - Affects {func.total_affected} function(s)
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ImpactSummaryTab;

